import React from "react";
import styled, { css, useTheme } from "styled-components";
import { BacktestingRunType, BotType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useBacktestingCard from "@shared/features/bots/BotForm/Backtesting/hooks.ts";
import Dropdown from "common/Dropdown/Dropdown";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import { openMessage } from "utils/misc";
import {
  newLookCardInteractive,
  newLookCardSelected,
  newLookCardSurface,
} from "features/bots/NewLook/newLookStyles";
import BacktestStatusTag from "features/bots/BotForm/Backtesting/common/BacktestStatusTag";
import useBacktestingActions from "features/bots/BotForm/Backtesting/common/useBacktestingActions";
import ActionsTrigger from "features/bots/BotForm/Backtesting/NewLook/ActionsTrigger";
import { BacktestIcon } from "features/bots/BotForm/Backtesting/NewLook/BacktestCells";
import useNetPnl from "features/bots/BotForm/Backtesting/NewLook/useNetPnl";
import {
  formatClock,
  formatDay,
  formatSignedPercent,
  formatTimeframe,
  getBacktestSubject,
  getTimeframeDays,
} from "features/bots/BotForm/Backtesting/NewLook/format";
import useBacktestMoney from "features/bots/BotForm/Backtesting/NewLook/useBacktestMoney";
import { phoneTwoLineTitle } from "common/NewLook/layout.ts";

interface NewLookBacktestCardProps {
  data?: BacktestingRunType;
  isSelected?: boolean;
  /** On the backtests page (rather than in the bot form's history). */
  isSeeAllPage?: boolean;
  botType: BotType;
  setBacktestingToDelete: (value: BacktestingRunType) => void;
}

/**
 * New look: a backtest as a card — name (and, on the backtests page, what it
 * tested and where), its net PnL as the lead figure next to the period, and
 * a footer with the status and when it ran. Hairline, no shadow; the applied
 * backtest is outlined in brand blue.
 */
const NewLookBacktestCard = ({
  data,
  isSelected,
  isSeeAllPage,
  botType,
  setBacktestingToDelete,
}: NewLookBacktestCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const tokens = getNewLookTokens(useTheme());
  const { signedMoney } = useBacktestMoney();
  const { menuItems, openExtraDetailsDrawer } = useBacktestingActions({
    data,
    openDeleteModal: () => data && setBacktestingToDelete(data),
    openMessage,
    isSelected,
    isBotForm: !isSeeAllPage,
    botType,
  });
  // Keeps the form's "values modified" state in sync with the applied run;
  // once the form drifts from it, the outline turns to the warning colour.
  const { borderColor } = useBacktestingCard({ isSelected });
  const isModified = !!isSelected && borderColor === "gold";
  const { value: pnl, percent } = useNetPnl(data);

  const meta = isSeeAllPage
    ? [getBacktestSubject(data, botType), data?.client_name].filter(Boolean)
    : [];
  const days = getTimeframeDays(data?.backtesting_time_range);
  const actionsLabel = t("backtesting.actions.backtest_actions");

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={data?.name}
      $selected={isSelected}
      $modified={isModified}
      onClick={() => openExtraDetailsDrawer()}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget)
          openExtraDetailsDrawer();
      }}
      data-bt-card={data?.id}
    >
      <Header>
        {isSeeAllPage && (
          <BacktestIcon run={data} botType={botType} size={32} />
        )}
        <Titles>
          <Name title={data?.name}>{data?.name}</Name>
          {meta.length > 0 && (
            <Sub title={meta.join(" · ")}>{meta.join(" · ")}</Sub>
          )}
        </Titles>
        <div
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Dropdown
            title={actionsLabel}
            menu={{ items: menuItems }}
            trigger={["click"]}
          >
            <ActionsTrigger label={actionsLabel} />
          </Dropdown>
        </div>
      </Header>
      <Figures>
        <Figure>
          <Label>{t("backtesting.labels.net_pnl")}</Label>
          <Lead style={{ color: directionColor(tokens, pnl) }}>
            {signedMoney(pnl)}
          </Lead>
          <Meta
            style={{ color: directionColor(tokens, percent) }}
            $strong={true}
          >
            {formatSignedPercent(percent)}
          </Meta>
        </Figure>
        <Figure>
          <Label>{t("backtesting.labels.timeframe")}</Label>
          <Value>{formatTimeframe(data?.backtesting_time_range)}</Value>
          {days !== undefined && (
            <Meta>
              {days} {t("backtesting.labels.days")}
            </Meta>
          )}
        </Figure>
      </Figures>
      <Footer>
        <BacktestStatusTag backtestingRun={data} />
        <Meta>
          {formatDay(data?.created_at)} · {formatClock(data?.created_at)}
        </Meta>
      </Footer>
    </Card>
  );
};

export default React.memo(NewLookBacktestCard);

const Card = styled.div<{ $selected?: boolean; $modified?: boolean }>`
  ${newLookCardSurface};
  ${newLookCardInteractive};
  ${(p) => p.$selected && newLookCardSelected};
  ${(p) =>
    p.$modified &&
    css`
      &,
      &:hover {
        box-shadow: inset 0 0 0 1.5px ${getNewLookTokens(p.theme).stop};
      }
    `};
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px 16px 0;
  text-align: left;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  margin: -4px -8px 0 0;
`;
const Titles = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const ellipsis = css`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Name = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  ${ellipsis};
  ${phoneTwoLineTitle};
`;
const Sub = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  ${ellipsis};
`;
const Figures = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 16px;
  margin-top: 16px;
`;
const Figure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Label = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  margin-bottom: 2px;
`;
const Lead = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
// The period may wrap (a year-long run reads "Sep 28, 2025 – Sep 28,
// 2026"); a short one sits on the lead figure's line.
const Value = styled.div`
  ${newLookType.body};
  padding: 3px 0;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const Meta = styled.div<{ $strong?: boolean }>`
  ${newLookType.meta};
  ${(p) => p.$strong && "font-weight: 500;"}
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
  margin-top: 16px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
